import { useCallback } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import {
  cancelMarketLimitSignal,
  canSendSignal,
  getComment,
  getPortfolio,
  getSandboxPortfolio,
  getSignalSettings,
  newDelayedSignal,
  newMarketLimitSignal,
  newMarketSignal,
  newMultipleMarketSignal,
  newPortfolioSignal,
} from '../../../../api';
import { ApiDataResponse } from '../../../../api/types/response';
import { DelayedSignal } from '../../../../types/delayedSignal';
import { PortfolioSignal } from '../../../../types/portfolioSignal';
import { StrategyStatus } from '../../../../types/strategy';
import { ApiStrategyPortfolio, StrategyPortfolio } from '../../../../types/strategyPortfolio';
import { MarketSignal } from '../AddSignal/types';

const normalizePortfolioWeight = (weight: string) => {
  const parsedWeight = Number(weight);
  return Number.isFinite(parsedWeight) ? parsedWeight : 0;
};

const normalizePortfolioResponse = (
  response: ApiDataResponse<ApiStrategyPortfolio>,
): ApiDataResponse<StrategyPortfolio> => ({
  ...response,
  result: {
    ...response.result,
    positions: response.result.positions.map((position) => ({
      ...position,
      weight: normalizePortfolioWeight(position.weight),
    })),
  },
});

export const usePortfolio = (id: string, strategyStatus: StrategyStatus | undefined) => {
  const queryFn = useCallback(() => {
    if (strategyStatus === StrategyStatus.Standard || strategyStatus === StrategyStatus.Closed) {
      return getPortfolio(id).then(normalizePortfolioResponse);
    } else {
      return getSandboxPortfolio(id).then(normalizePortfolioResponse);
    }
  }, [id, strategyStatus]);

  const { data, refetch, isLoading, error } = useQuery({
    queryKey: ['portfolio', id, strategyStatus],
    queryFn: queryFn,
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
    enabled: !!id,
  });

  return { data, refetch, isLoading, error };
};

export const useCheckSignal = (strategyId: string, executeImmediately: boolean = true) => {
  return useQuery({
    queryKey: ['checkSignal', strategyId],
    queryFn: () => canSendSignal(strategyId),
    enabled: executeImmediately && Boolean(strategyId),
    refetchInterval: (query) => {
      const executingNumber = query.state.data?.result.executingNumber ?? 0;
      return executingNumber > 0 ? 10000 : false;
    },
  });
};

type BaseSignalQueryHookProps = {
  onSuccess: (result: ApiDataResponse<unknown>) => void;
  onError: (e: unknown) => void;
};

export const useMultipleMarketSignals = ({ onSuccess, onError }: BaseSignalQueryHookProps) => {
  return useMutation({
    mutationFn: (signals: MarketSignal[]) => newMultipleMarketSignal(signals),
    onSuccess,
    onError: onError,
  });
};

export const usePortfolioSignal = ({ onSuccess, onError }: BaseSignalQueryHookProps) => {
  return useMutation({
    mutationFn: (signal: PortfolioSignal) => newPortfolioSignal(signal),
    onSuccess,
    onError: onError,
  });
};

export const useMarketSignal = ({ onSuccess, onError }: BaseSignalQueryHookProps) => {
  return useMutation({
    mutationFn: (signal: MarketSignal) => newMarketSignal(signal),
    onSuccess,
    onError: onError,
  });
};

export const useMarketLimitSignal = ({ onSuccess, onError }: BaseSignalQueryHookProps) => {
  return useMutation({
    mutationFn: (signal: MarketSignal) => newMarketLimitSignal(signal),
    onSuccess,
    onError: onError,
  });
};

export const useDelayedSignal = ({ onSuccess, onError }: BaseSignalQueryHookProps) => {
  return useMutation({
    mutationFn: (signal: Partial<DelayedSignal>) => newDelayedSignal(signal),
    onSuccess,
    onError: onError,
  });
};

export const useComment = (strategyId: string, date: string) => {
  return useQuery({ queryKey: ['comment', strategyId, date], queryFn: () => getComment(strategyId, date) });
};

export const useSignalSettings = (enabled: boolean) => {
  return useQuery({ queryKey: ['signalSettings'], queryFn: getSignalSettings, enabled });
};

export const useCancelMarketLimitSignal = ({ onSuccess, onError }: BaseSignalQueryHookProps) => {
  return useMutation({
    mutationFn: (params: { strategyId: string; managerId: number }) => cancelMarketLimitSignal(params),
    onSuccess,
    onError: onError,
  });
};
